import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import { Alert, Avatar, CircularProgress, Grid, Typography } from '@mui/material'

import { Box } from '@/components/ui'
import { useLocations } from '@/hooks/useLocations'
import { PageCondensed } from '@/layouts'

import { useRouteOrg } from '../../orgRoute'
import { LocationCard } from './components/LocationCard'
import { buildLocationRows } from './utils/buildLocationRows'

const LOCATIONS_UNREADABLE_MESSAGE =
  'This organization’s locations could not be loaded. Reload to try again.'

const NO_LOCATIONS_MESSAGE =
  'This organization has no location yet, so it has no address, phone, or time zone ' +
  'on record. Its primary location is created as part of setting the organization up — ' +
  'use Resume Setup on the Overview tab to add it.'

/**
 * An organization's locations, read-only.
 *
 * One request carries every location and its full detail, so the tab either has
 * all of them or reports the one failure. The primary location is whichever one
 * the organization points at, so that comes from the organization rather than the
 * location.
 *
 * There is no create control here: the primary location belongs to the setup flow,
 * which is the only thing that can create one, and every location after it is the
 * practice's own to add in Bartimaeus.
 */
export const LocationsTab = () => {
  const org = useRouteOrg()

  const { data: locations, isLoading, isError } = useLocations(org.id)

  const rows = buildLocationRows(locations ?? [], org.primary_location_id)
  const hasNoLocations = !isLoading && !isError && rows.length === 0

  return (
    <PageCondensed>
      <Box variant='flexColumn' gap='lg'>
        <Box variant='flex' gap='md'>
          <Avatar variant='titleIcon'>
            <PlaceOutlinedIcon />
          </Avatar>
          <Typography variant='h4' fontWeight={600}>
            Locations
          </Typography>
        </Box>

        {isLoading && (
          <Box variant='flexCenter'>
            <CircularProgress />
          </Box>
        )}

        {isError && <Alert severity='error'>{LOCATIONS_UNREADABLE_MESSAGE}</Alert>}

        {hasNoLocations && (
          <Typography variant='body2' color='text.secondary'>
            {NO_LOCATIONS_MESSAGE}
          </Typography>
        )}

        {/* Two cards per row on anything wider than a phone; one below that. */}
        {rows.length > 0 && (
          <Grid container spacing={2}>
            {rows.map((row) => (
              <Grid key={row.location.id} size={{ xs: 12, md: 6 }}>
                <LocationCard row={row} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </PageCondensed>
  )
}
